import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { PatientAppointmentService } from './../../../core/services/patient-appointment.service';
import {
  AuthorisedCode,
  CoverageWarning,
  LegacyAuthorization,
  PatientAuthorization,
  PatientAuthorizationService
} from './../../../core/services/patient-authorization.service';
import { AUTHORISATION_STATUS_LABELS, authorisationValidity } from './../../../core/utils/authorisation-summary';

/** The dialog's editing model. Dates are `Date` because `mat-datepicker` binds them. */
interface AuthorizationDraft {
  id: string | null;
  authNumber: string;
  schemeName: string;
  schemeNumber: string;
  authorisedFrom: Date | null;
  authorisedTo: Date | null;
  authorisedDays: number | null;
  /** Free text, `1234:2, 5678`. Parsed on the server so one parser exists, not two. */
  authorisedCodesText: string;
  appointmentId: string | null;
  status: 'requested' | 'approved' | 'declined' | 'expired';
  notes: string;
}

/**
 * One row of the list: a recorded authorisation, or a number still stored on a patient or
 * admission record (`legacy`), shown in the same shape so the practice sees one list.
 */
export interface AuthorizationRow {
  authorization: PatientAuthorization;
  legacy: LegacyAuthorization | null;
}

const STATUS_LABELS = AUTHORISATION_STATUS_LABELS;

/** Coverage warnings left out while the list has no way to capture authorised codes. */
const HIDDEN_WARNINGS: CoverageWarning['type'][] = ['authorisation-has-no-codes'];

/** Just enough of an appointment to choose one. */
interface AppointmentOption {
  id: string;
  label: string;
}

/**
 * The authorisation list, against the REAL TABLE — tasks B3 and B4 of
 * `tasks/theatre-booking-and-procedure-lifecycle.md`.
 *
 * ── Numbers stored on the patient record ──
 *
 * `Patient.authorizationNumbers` is an encrypted JSON array. That blob
 * is still, for every existing practice, the only place their authorisations live:
 * `scripts/backfill-patient-authorizations.js` is written and proven but HAS NOT BEEN RUN, and
 * running it creates 12,903 rows — the repository owner's decision, not this task's.
 *
 * Those stored numbers are shown as rows of this one list, in the same shape as recorded
 * authorisations, with no migration. Editing one records it as a real authorisation; the server
 * drops any blob entry whose number already exists in the table (compared by blind index, the
 * only way to compare a random-IV encrypted column), so it then leaves the list's stored part.
 * Removing one from the patient record is handed to the host (`legacyRemoved`), whose form saves
 * the blob with the patient. Numbers on an admission record can be edited but not removed here.
 *
 * ── B4: warn, never block ────────────────────────────────────────────────────────────
 *
 * The coverage banner reports codes no authorisation covers and authorisations that expired
 * before the date of service. It is rendered beside the list and disables nothing. §7 of the
 * task doc is explicit — "Warn — never block" — and the server hard-codes `blocked: false`, so
 * there is no shape of response this component could read as a veto even if it wanted to.
 */
@Component({
  selector: 'app-authorization-list',
  templateUrl: './authorization-list.component.html',
  styleUrls: ['./authorization-list.component.scss']
})
export class AuthorizationListComponent extends BaseComponent implements OnChanges {
  /** Required. Without a saved patient there is no row to hang an authorisation on. */
  @Input() patientId: string | null = null;
  @Input() groupId: string | null = null;
  /** Pre-selects the booking when this list is opened from one. */
  @Input() appointmentId: string | null = null;
  @Input() admissionId: string | null = null;
  /** The day the work happens; drives the expiry half of the coverage check. */
  @Input() dateOfService: string | Date | null = null;
  /** Set false where the hosting screen has no room for the offer to scope. */
  @Input() allowAppointmentScope = true;

  /** Fires after any write, so a host can refresh anything derived from authorisations. */
  @Output() authorizationsChanged = new EventEmitter<PatientAuthorization[]>();
  /**
   * A number stored on the patient record was removed. The host takes it out of the patient
   * form's `authorizationNumbers`, which saves with the patient.
   */
  @Output() legacyRemoved = new EventEmitter<LegacyAuthorization>();

  authorizations: PatientAuthorization[] = [];
  legacy: LegacyAuthorization[] = [];
  appointments: AppointmentOption[] = [];
  warnings: CoverageWarning[] = [];

  loading = false;
  saving = false;
  loadFailed = false;

  readonly statuses = (Object.keys(STATUS_LABELS) as Array<AuthorizationDraft['status']>)
    .map(value => ({ value, label: STATUS_LABELS[value] }));

  /** Recorded authorisations first, then the numbers still stored on records. */
  get rows(): AuthorizationRow[] {
    return [
      ...this.authorizations.map(authorization => ({ authorization, legacy: null })),
      ...this.legacy.map(legacy => ({ authorization: this.legacyAsAuthorization(legacy), legacy })),
    ];
  }

  draft: AuthorizationDraft = this.emptyDraft();

  @ViewChild('authorizationDialog') authorizationDialog?: TemplateRef<any>;

  constructor(
    fs: FeedbackService,
    md: MatDialog,
    private authorizationService: PatientAuthorizationService,
    private appointmentService: PatientAppointmentService
  ) {
    super(fs, md);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patientId']) {
      this.authorizations = [];
      this.legacy = [];
      this.warnings = [];
      this.appointments = [];
      if (this.patientId) {
        this.load();
        this.loadAppointments();
      }
    } else if ((changes['dateOfService'] || changes['appointmentId']) && this.patientId) {
      this.refreshCoverage();
    }
  }

  // ── Reading ────────────────────────────────────────────────────────────────────────

  /** Table rows and un-migrated blob entries, then the advisory coverage check over them. */
  load(): void {
    if (!this.patientId) { return; }
    this.loading = true;
    this.loadFailed = false;

    this.authorizationService.loadByPatient(this.patientId).subscribe({
      next: (response) => {
        this.loading = false;
        this.authorizations = (response && response.data) || [];
        this.legacy = (response && response.legacy) || [];
        this.authorizationsChanged.emit(this.authorizations);
        this.refreshCoverage();
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      }
    });
  }

  private loadAppointments(): void {
    if (!this.patientId || !this.allowAppointmentScope) { return; }
    this.appointmentService.loadAppointmentsByPatient(this.patientId).subscribe({
      next: (response: any) => {
        // The endpoint answers { data: [...] }.
        const rows = Array.isArray(response) ? response : (response?.data || []);
        this.appointments = rows
          .filter((row: any) => row && row.id)
          .map((row: any) => ({
            id: row.id,
            label: [
              row.date ? moment(row.date).format('DD/MM/YYYY') : '',
              row.timeSlot || '',
              row.procedure || row.appointmentType || ''
            ].filter(Boolean).join(' · ')
          }));
      },
      // An appointment list that will not load must not take out the authorisation list; it only
      // costs the scope picker its options.
      error: () => { this.appointments = []; }
    });
  }

  /**
   * B4. Advisory only — a failure here clears the banner rather than showing an error, because
   * "we could not check" is not the same claim as "there is a problem" (`lessons.md` §14).
   */
  refreshCoverage(): void {
    if (!this.patientId) { this.warnings = []; return; }
    this.authorizationService.checkCoverage({
      patientId: this.patientId,
      appointmentId: this.appointmentId || null,
      dateOfService: this.serviceDate()
    }).subscribe({
      next: (response) => { this.warnings = this.shownWarnings(response && response.warnings); },
      error: () => { this.warnings = []; }
    });
  }

  // ── Writing ────────────────────────────────────────────────────────────────────────

  openAdd(): void {
    if (!this.patientId || !this.authorizationDialog) { return; }
    this.draft = this.emptyDraft();
    this.draft.appointmentId = this.appointmentId || null;
    this.dialog.open(this.authorizationDialog, { width: '640px' });
  }

  /** A stored number opens as a new authorisation, filled from what the record holds. */
  openEdit(row: AuthorizationRow): void {
    if (!row || !this.authorizationDialog) { return; }
    const authorization = row.authorization;
    this.draft = {
      id: row.legacy ? null : authorization.id,
      authNumber: authorization.authNumber || '',
      schemeName: authorization.schemeName || '',
      schemeNumber: authorization.schemeNumber || '',
      authorisedFrom: authorization.authorisedFrom ? moment(authorization.authorisedFrom).toDate() : null,
      authorisedTo: authorization.authorisedTo ? moment(authorization.authorisedTo).toDate() : null,
      authorisedDays: authorization.authorisedDays ?? null,
      authorisedCodesText: this.codesToText(authorization.authorisedCodes),
      appointmentId: authorization.appointmentId || null,
      status: authorization.status || 'approved',
      notes: authorization.notes || ''
    };
    this.dialog.open(this.authorizationDialog, { width: '640px' });
  }

  save(): void {
    if (!this.patientId || this.saving) { return; }

    const authNumber = (this.draft.authNumber || '').trim();
    if (!authNumber) {
      this.showToastMessage('An authorisation number is required');
      return;
    }

    // Sent as typed. The server owns the `"1234:2"` parse so there is exactly one parser, and
    // it is the one the coverage check reads back.
    const payload: any = {
      patientId: this.patientId,
      authNumber,
      schemeName: (this.draft.schemeName || '').trim() || null,
      schemeNumber: (this.draft.schemeNumber || '').trim() || null,
      authorisedFrom: this.toIsoDate(this.draft.authorisedFrom),
      authorisedTo: this.toIsoDate(this.draft.authorisedTo),
      authorisedDays: this.draft.authorisedDays ?? null,
      authorisedCodes: this.draft.authorisedCodesText || '',
      appointmentId: this.draft.appointmentId || null,
      admissionId: this.admissionId || null,
      status: this.draft.status || 'approved',
      notes: (this.draft.notes || '').trim() || null,
      dateOfService: this.serviceDate()
    };

    this.saving = true;
    const request$ = this.draft.id
      ? this.authorizationService.update(this.draft.id, payload)
      : this.authorizationService.create(payload);

    request$.subscribe({
      next: (response) => {
        this.saving = false;
        this.dialog.closeAll();
        // The write ALWAYS succeeds — coverage warnings ride along with the 201 and are shown
        // afterwards. Nothing here inspects them to decide whether to save.
        if (response && response.coverage && response.coverage.warnings) {
          this.warnings = this.shownWarnings(response.coverage.warnings);
        }
        this.showToastMessage(this.draft.id ? 'Authorisation updated' : 'Authorisation recorded');
        this.load();
      },
      error: (error) => {
        this.saving = false;
        this.showToastMessage((error && error.error && error.error.message) || 'Could not save the authorisation');
      }
    });
  }

  cancel(): void {
    this.dialog.closeAll();
  }

  /** Only numbers on the patient record can be removed here, and only when the host saves them. */
  canRemove(row: AuthorizationRow): boolean {
    if (!row.legacy) { return true; }
    return row.legacy.origin === 'patient' && this.legacyRemoved.observed;
  }

  async remove(row: AuthorizationRow): Promise<void> {
    if (!row) { return; }
    if (!row.legacy) { return this.deactivate(row.authorization); }
    if (!this.canRemove(row)) { return; }
    const confirmed = await this.showConfirmationDialog(
      'REMOVE AUTHORISATION',
      `Remove authorisation ${row.legacy.authNumber}? It is taken off the patient record when the patient is saved.`,
      undefined, 'Cancel', 'Remove', true);
    if (!confirmed) { return; }
    this.legacy = this.legacy.filter(entry => entry !== row.legacy);
    this.legacyRemoved.emit(row.legacy);
  }

  async deactivate(authorization: PatientAuthorization): Promise<void> {
    if (!authorization) { return; }
    const confirmed = await this.showConfirmationDialog(
      'REMOVE AUTHORISATION',
      `Remove authorisation ${authorization.authNumber}? It stays on the record for audit, `
      + 'but will no longer be used for coverage checks or theatre bookings.',
      undefined, 'Cancel', 'Remove', true);
    if (!confirmed) { return; }

    this.authorizationService.deactivate(authorization.id).subscribe({
      next: () => { this.showToastMessage('Authorisation removed'); this.load(); },
      error: () => { this.showToastMessage('Could not remove the authorisation'); }
    });
  }

  /** B3's headline: give an authorisation a durable link to a booking. */
  scope(authorization: PatientAuthorization, appointmentId: string | null): void {
    if (!authorization) { return; }
    this.authorizationService.scopeToAppointment(authorization.id, appointmentId || null).subscribe({
      next: () => {
        this.showToastMessage(appointmentId ? 'Authorisation linked to the booking' : 'Booking link removed');
        this.load();
      },
      error: () => { this.showToastMessage('Could not link the authorisation'); }
    });
  }

  // ── Display helpers ────────────────────────────────────────────────────────────────

  statusLabel(status: string | null | undefined): string {
    return STATUS_LABELS[status as AuthorizationDraft['status']] || '';
  }

  validityLabel(authorization: PatientAuthorization): string {
    if (!authorization) { return ''; }
    return authorisationValidity(authorization) || 'no validity dates recorded';
  }

  codesLabel(authorization: PatientAuthorization): string {
    const text = this.codesToText(authorization && authorization.authorisedCodes);
    return text || 'no codes recorded';
  }

  appointmentLabel(appointmentId: string | null): string {
    if (!appointmentId) { return ''; }
    const match = this.appointments.find((option) => option.id === appointmentId);
    return match ? match.label : 'linked booking';
  }

  /** Warnings that name a specific authorisation, so the row can carry its own marker. */
  warningsForAuthorization(authorizationId: string): CoverageWarning[] {
    return this.warnings.filter((warning) => warning && warning.authorizationId === authorizationId);
  }

  trackByRow(_index: number, row: AuthorizationRow): string {
    return row.authorization.id;
  }

  trackByWarning(index: number, warning: CoverageWarning): string {
    return warning ? `${warning.type}:${warning.code || warning.authorizationId || index}` : String(index);
  }

  // ── Internals ──────────────────────────────────────────────────────────────────────

  private emptyDraft(): AuthorizationDraft {
    return {
      id: null,
      authNumber: '',
      schemeName: '',
      schemeNumber: '',
      authorisedFrom: null,
      authorisedTo: null,
      authorisedDays: null,
      authorisedCodesText: '',
      appointmentId: null,
      status: 'approved',
      notes: ''
    };
  }

  /** A stored number in the shape of an authorisation: its date as the start, its comments as notes. */
  private legacyAsAuthorization(entry: LegacyAuthorization): PatientAuthorization {
    return {
      id: `legacy:${entry.origin}:${entry.originId}:${entry.authNumber}`,
      patientId: this.patientId || '',
      groupId: this.groupId || '',
      authNumber: entry.authNumber,
      schemeName: null,
      schemeNumber: null,
      authorisedFrom: entry.date ? moment(entry.date).format('YYYY-MM-DD') : null,
      authorisedTo: null,
      authorisedDays: null,
      authorisedCodes: [],
      appointmentId: null,
      admissionId: null,
      status: 'approved',
      notes: entry.comments || null,
      active: true,
    };
  }

  private shownWarnings(warnings: CoverageWarning[] | null | undefined): CoverageWarning[] {
    return (warnings || []).filter(warning => warning && !HIDDEN_WARNINGS.includes(warning.type));
  }

  private codesToText(codes: AuthorisedCode[] | null | undefined): string {
    if (!Array.isArray(codes) || !codes.length) { return ''; }
    return codes
      .filter((entry) => entry && entry.code)
      .map((entry) => (entry.qty && entry.qty > 1 ? `${entry.code}:${entry.qty}` : entry.code))
      .join(', ');
  }

  private toIsoDate(value: Date | null): string | null {
    return value ? moment(value).format('YYYY-MM-DD') : null;
  }

  /** The date the coverage check is made against — the host's, or today. */
  private serviceDate(): string {
    return this.dateOfService
      ? moment(this.dateOfService).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');
  }
}
